import React from 'react';
import { Column, Row, Section } from '@ydistri/ds';
import {
  useGetApplicationHintsQuery,
  useGetCategoryConfigurationDefinitionsQuery,
} from '../../../../apis/apiApplication';
import { Spin } from 'antd';
import {
  ApplicationHintType,
  CategoryConfigurationDefinitionResponse,
  ConfigurationSectionType,
} from '@ydistri/api-sdk';
import { useGetCategoryTreeQuery } from '../../../../components/global/CategoryTree/apiCategoryTree';
import { useGetRootCategoryConfigurationQuery } from '../../apiTemplate';
import { useStableComputedValue } from '../../../../hooks/useStableComputedValue';
import CategoryConfigurationHeader from './components/CategoryConfigurationHeader';
import { useTemplateOrCalculationWithValue } from '../../../../hooks/useTemplateOrCalculationWithValue';
import CategoryConfigurationPane from './CategoryConfigurationPane';

export type CategoryConfigurationDefinitionGroup = Record<
  ConfigurationSectionType,
  CategoryConfigurationDefinitionResponse[]
>;

const SegmentPotentialDefinition: React.FC = () => {
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { data: configurationDefinitions, isFetching: isFetchingDefinitions } =
    useGetCategoryConfigurationDefinitionsQuery();
  const { data: hints, isFetching: isFetchingHints } = useGetApplicationHintsQuery();
  const { isFetching: isFetchingCategoryTree } = useGetCategoryTreeQuery(
    templateOrCalculation.value,
  );
  const { isFetching: isFetchingRootCategoryConfiguration } = useGetRootCategoryConfigurationQuery(
    templateOrCalculation.value,
  );

  const isFetchingConfigurationGroups = isFetchingHints || isFetchingDefinitions;

  const isFetching =
    isFetchingConfigurationGroups || isFetchingCategoryTree || isFetchingRootCategoryConfiguration;

  const definitionGroups = useStableComputedValue(
    () => {
      const groups: CategoryConfigurationDefinitionGroup = {
        [ConfigurationSectionType.General]: [],
        [ConfigurationSectionType.PotentialSource]: [],
        [ConfigurationSectionType.PotentialSourceStandardSubsection]: [],
        [ConfigurationSectionType.PotentialSourceForcedSubsection]: [],
        [ConfigurationSectionType.PotentialTarget]: [],
      };

      if (configurationDefinitions) {
        [...configurationDefinitions]
          .sort((a, b) => a.position - b.position)
          .forEach(definition => {
            const sectionType = definition.sectionType;

            const applicableHints = hints?.filter(
              hint => hint.key === definition.fieldType.toString(),
            );

            const description = applicableHints?.find(
              hint =>
                hint.applicationHintType ===
                ApplicationHintType.CategoryConfigurationDefinitionDescription,
            )?.value;

            const title = applicableHints?.find(
              hint =>
                hint.applicationHintType ===
                ApplicationHintType.CategoryConfigurationDefinitionTitle,
            )?.value;

            groups[sectionType].push({
              ...definition,
              title: title ?? definition.title,
              description: description ?? definition.description,
            });
          });

        return groups;
      }
    },
    isFetchingConfigurationGroups,
    [configurationDefinitions, hints],
  );

  return (
    <Column $flexGrow={1}>
      <CategoryConfigurationHeader busy={isFetching} />
      <Row>
        <Column $flexGrow={1} $flexWrap="nowrap">
          <Section
            header="Standard source potential definition"
            $ratio={0}
            $contentMarginTop={false}
          >
            <Spin spinning={isFetching}>
              <CategoryConfigurationPane
                sectionType={ConfigurationSectionType.PotentialSourceStandardSubsection}
                definitionGroups={definitionGroups}
              />
            </Spin>
          </Section>
          <Section header="Forced source potential definition" $ratio={0} $contentMarginTop={false}>
            <Spin spinning={isFetching}>
              <CategoryConfigurationPane
                sectionType={ConfigurationSectionType.PotentialSourceForcedSubsection}
                definitionGroups={definitionGroups}
              />
            </Spin>
          </Section>
        </Column>
        <Column $flexGrow={1} $flexWrap="nowrap">
          <Section header="Target potential definition" $contentMarginTop={false} $ratio={0}>
            <Spin spinning={isFetching}>
              <CategoryConfigurationPane
                sectionType={ConfigurationSectionType.PotentialTarget}
                definitionGroups={definitionGroups}
                displayTitle={false}
              />
            </Spin>
          </Section>
        </Column>
      </Row>
    </Column>
  );
};

export default SegmentPotentialDefinition;
